// TransferTable.tsx
import React, { useMemo, useCallback, useState, useEffect } from "react";
import {
  Table,
  Tag,
  Space,
  Typography,
  Tooltip,
  Input,
  message,
  Button,
  Card,
  Modal,
  theme,
  Pagination
} from "antd";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  LinkOutlined,
  StopOutlined,
  UndoOutlined
} from "@ant-design/icons";
import { format } from "date-fns";
import axios from "axios";
import styled from "styled-components";
import { API_BASE_URL } from "../../constants/config";
import { ProcessSteps } from "../ProcessSteps";
import type { Transfer, TransferTableProps } from "./types/wallet";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "react-responsive";
import { useUserPanel } from "../../contexts/UserPanelContext";
import { getAndSanitize } from "../../shared/utils/sanitization";

const { Text } = Typography;

const { useToken } = theme;

const PLATFORM_OPTIONS = [
  "mercadopago",
  "mercadopagoAlt",
  "naranjax",
  "personalpay",
  "personalpayAlt",
  "cuentadni",
  "uala",
  "galicia",
  "supervielle",
  "bna",
  "astropay",
  "astropayAlt",
  "ripio",
  "rioja",
  "credicoop",
  "belo",
  "brubank",
  "macro",
  "nbch",
  "bancosol",
  "prex",
] as const;

const CompactTable = styled(Table)`
  .ant-table-thead > tr > th {
    padding: 8px 16px;
  }
  .ant-table-tbody > tr > td {
    padding: 4px 16px;
    white-space: nowrap;
  }
  .ant-table-row {
    cursor: pointer;
  }
  .ant-table-expanded-row > td {
    padding: 8px 16px;
    background: #fafafa;
  }
` as typeof Table;

const CompactTag = styled(Tag)`
  margin: 0 4px 0 0;
  padding: 0 6px;
  font-size: 12px;
  line-height: 18px;
`;

const CompactSpace = styled(Space)`
  gap: 4px !important;
`;

// Definir SelectWithCustom para que esté disponible en todo el componente


export const TransferTable: React.FC<TransferTableProps> = ({ 
  transfers, 
  isLoading,
  pagination
}) => {
  const { t } = useTranslation();
  const isTabletOrMobile = useMediaQuery({ query: "(max-width: 1224px)" });
  const [discontinuingId, setDiscontinuingId] = useState<number | null>(null);
  const [updatingPlatform, setUpdatingPlatform] = useState(false);
  const [localTransfers, setLocalTransfers] = useState<Transfer[]>([]);
  const { currentPanelId } = useUserPanel();
  const [adminId, setAdminId] = useState<string | null>(null);
  const [platformModalVisible, setPlatformModalVisible] = useState(false);
  const [currentPlatformTransfer, setCurrentPlatformTransfer] = useState<Transfer | null>(null);
  const [customPlatform, setCustomPlatform] = useState("");
  const { token } = useToken();
  
  useEffect(() => {
    // Try to get adminId from token if stored
    try {
      const token = getAndSanitize("token");
      if (token) {
        const decodedToken = JSON.parse(atob(token.split(".")[1]));
        setAdminId(decodedToken.userId);
      }
    } catch (e) {
      console.error("Error getting adminId from token:", e);
    }
  }, []);
  
  // Determine effective adminId (prefer currentPanelId, fall back to localStorage)
  const effectiveAdminId = currentPanelId || adminId || getAndSanitize("adminId") || "";
  
  useEffect(() => {
    setLocalTransfers(transfers || []);
  }, [transfers]);
  
  const handleDiscontinueToggle = async (
    transferId: number,
    isDiscontinued: boolean
  ) => {
    try {
      setDiscontinuingId(transferId);
      const response = await axios.patch(
        `${API_BASE_URL}/api/transfers/${transferId}/discontinue`,
        { isDiscontinued: !isDiscontinued },
        {
          headers: {
            Authorization: `Bearer ${getAndSanitize("token")}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        // Actualizar localmente la transferencia
        const updatedTransfers = localTransfers.map(transfer => 
          transfer.id === transferId 
            ? { ...transfer, isDiscontinued: !isDiscontinued } 
            : transfer
        );
        
        // Actualizar el estado local
        setLocalTransfers(updatedTransfers);
        
        message.success(
          isDiscontinued
            ? t("wallet.transferTable.messages.transferRestoredSuccess")
            : t("wallet.transferTable.messages.transferDiscontinuedSuccess")
        );
      } else {
        message.error(
          response.data.message ||
            t("wallet.transferTable.messages.statusUpdateFailed")
        );
      }
    } catch (error) {
      console.error("Status update failed:", error);
      message.error(
        error.response?.data?.message ||
          t("wallet.transferTable.messages.statusUpdateFailed")
      );
    } finally {
      setDiscontinuingId(null);
    }
  };

  const handlePlatformChange = async (
    id: number,
    platform: string
  ) => {
    try {
      setUpdatingPlatform(true);
      const response = await axios.patch(
        `${API_BASE_URL}/api/transfers/${id}/platform`,
        { platform },
        {
          headers: {
            Authorization: `Bearer ${getAndSanitize("token")}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        // Actualizar el estado local con la nueva plataforma
        const updatedTransfers = localTransfers.map(transfer => 
          transfer.id === id 
            ? { ...transfer, platform } 
            : transfer
        );
        
        // Aplicar actualización al estado local
        setLocalTransfers(updatedTransfers);
        
        message.success(
          t("wallet.transferTable.messages.platformUpdateSuccess")
        );
      } else {
        message.error(
          response.data.message ||
            t("wallet.transferTable.messages.platformUpdateFailed")
        );
      }
    } catch (error) {
      console.error("Platform update failed:", error);
      message.error(
        error.response?.data?.message ||
          t("wallet.transferTable.messages.platformUpdateFailed")
      );
    } finally {
      setUpdatingPlatform(false);
      setPlatformModalVisible(false);
      setCurrentPlatformTransfer(null);
      setCustomPlatform("");
    }
  };

  // Manejador para abrir el modal de plataforma
  const handleOpenPlatformModal = (transfer: Transfer) => {
    setCurrentPlatformTransfer(transfer);
    // Si ya tiene plataforma, inicializa el campo de plataforma personalizada con su valor
    if (transfer.platform) {
      setCustomPlatform(transfer.platform);
    } else {
      setCustomPlatform("");
    }
    setPlatformModalVisible(true);
  };

  // Definir MobileTransferCard dentro del componente principal
const MobileTransferCard: React.FC<{ transfer: Transfer }> = useCallback(({ transfer }) => {
  
  // Create a stable cache key using the transfer ID
  
  return (
    <Card 
      style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center' 
      }}
      styles={{
        body: { padding: '16px' }
      }}
      bordered={true}
    >
      {/* Encabezado con información principal destacada */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 14
      }}>
        <div>
          {(() => {
            // Para TelePagos, CUIT/CUIL tiene mayor prioridad que operationNumber
            const isTelePagos = transfer.operationNumber?.startsWith('TP-');
            const identifier = isTelePagos
              ? (transfer.coelsaId || transfer.cuit || transfer.cuil || transfer.operationNumber)
              : (transfer.coelsaId || transfer.operationNumber || transfer.cuit || transfer.cuil);


            return (
              <CompactTag color="volcano" style={{ fontSize: '14px', padding: '2px 8px' }}>
                {identifier}
              </CompactTag>
            );
          })()}
        </div>
        <div>
          {transfer.validatedReceiptHash ? (
            <CompactTag icon={<CheckCircleOutlined />} color="success" style={{ fontSize: '14px', padding: '2px 8px' }}>
              {t("wallet.transferTable.tags.validated")}
            </CompactTag>
          ) : transfer.isDiscontinued ? (
            <CompactTag icon={<StopOutlined />} color="error" style={{ fontSize: '14px', padding: '2px 8px' }}>
              {t("wallet.transferTable.tags.discontinued")}
            </CompactTag>
          ) : (
            <CompactTag icon={<ClockCircleOutlined />} style={{ fontSize: '14px', padding: '2px 8px' }}>
              {t("wallet.transferTable.tags.pending")}
            </CompactTag>
          )}
        </div>
      </div>

      {/* Información principal destacada */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
            {t("wallet.transferTable.table.amount")}
          </Text>
          <Text strong style={{ color: token.colorSuccess, fontSize: '20px' }}>
            ${transfer.amount.toFixed(2)}
          </Text>
          {transfer.validatedAmount &&
            transfer.validatedAmount !== transfer.amount && (
              <div style={{ marginTop: 4 }}>
                <CompactTag color="orange">
                  {t("wallet.transferTable.tags.val")}: ${transfer.validatedAmount.toFixed(2)}
                </CompactTag>
              </div>
            )}
        </div>
        <div style={{ textAlign: 'right' }}>
          <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
            {t("wallet.transferTable.table.platform")}
          </Text>
          <Tag
            color="blue"
            style={{ cursor: "pointer", marginTop: 2 }}
            className="platform-tag"
            data-transfer-id={transfer.id}
            onClick={(e) => {
              e.stopPropagation();
              // Elimino la verificación para que funcione siempre que no esté validado o vinculado
              if (!transfer.validatedReceiptHash && !transfer.linkedOperations.length) {
                handleOpenPlatformModal(transfer);
              }
            }}
          >
            {transfer.platform ||
              t("wallet.transferTable.platformSelect.setPlatform")}
          </Tag>
        </div>
      </div>

      {/* Información secundaria en formato más compacto */}
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: 12 }}>
        <div>
          <Text type="secondary" style={{ display: 'block' }}>
            {t("wallet.transferTable.table.date")}
          </Text>
          <Text>
            {format(new Date(transfer.dateTime), "dd/MM/yy HH:mm")}
          </Text>
        </div>
        {transfer.validationScore && (
          <div style={{ textAlign: 'right' }}>
            <Text type="secondary" style={{ display: 'block' }}>
              {t("wallet.transferTable.table.score")}
            </Text>
            <CompactTag
              color={transfer.validationScore > 0.8 ? "green" : "orange"}
              style={{ width: "45px", textAlign: "center" }}
            >
              {Math.round(transfer.validationScore * 100)}%
            </CompactTag>
          </div>
        )}
      </div>

      {/* Operaciones vinculadas */}
      {transfer.linkedOperations.length > 0 && (
        <div style={{ 
          marginTop: 10, 
          padding: 8, 
          background: token.colorInfoBg, 
          borderRadius: 4,
          borderLeft: `3px solid ${token.colorInfo}` 
        }}>
          <Tooltip title={t("wallet.transferTable.tooltips.linkedOperations", { count: transfer.linkedOperations.length })}>
            <CompactTag icon={<LinkOutlined />} color="cyan" style={{ marginBottom: 4 }}>
              {transfer.linkedOperations.length} {t("wallet.transferTable.tags.linkedOperations")}
            </CompactTag>
          </Tooltip>
        </div>
      )}
      
      {/* Solicitudes vinculadas */}
    {transfer.linkedRequests?.length > 0 && (
      <div 
        style={{ 
          marginTop: 16, 
          padding: '10px 10px 5px 10px',
          background: token.colorBgElevated, 
          borderRadius: 4,
          borderLeft: `3px solid ${token.colorPrimary}`,
          marginBottom: 16
        }}
      >
        {/* Contenido de linkedRequests aquí */}
      </div>
    )}
    </Card>
  );
}, [discontinuingId, handleDiscontinueToggle, t, localTransfers, handleOpenPlatformModal, token, effectiveAdminId]);


  const getRowKey = useCallback((record: Transfer): number => record.id, []);

  const renderPlatform = useCallback(
    (record: Transfer) => {
      return (
        <Tag
          color="blue"
          style={{ cursor: "pointer" }}
          className="platform-tag"
          data-transfer-id={record.id}
          onClick={(e) => {
            e.stopPropagation();
            // Elimino la verificación para que funcione siempre que no esté validado o vinculado
            if (!record.validatedReceiptHash && !record.linkedOperations.length) {
              handleOpenPlatformModal(record);
            }
          }}
        >
          {record.platform ||
            t("wallet.transferTable.platformSelect.setPlatform")}
        </Tag>
      );
    },
    [t, handleOpenPlatformModal]
  );

  const renderOperation = useCallback(
    (record: Transfer) => {
      // Para TelePagos, CUIT/CUIL tiene mayor prioridad que operationNumber
      const isTelePagos = record.operationNumber?.startsWith('TP-');
      const identifier = isTelePagos
        ? (record.coelsaId || record.cuit || record.cuil || record.operationNumber)
        : (record.coelsaId || record.operationNumber || record.cuit || record.cuil);


      return (
        <CompactSpace>
          <CompactTag color="volcano">{identifier}</CompactTag>
          {record.platform && (
            <Tooltip title={t("wallet.transferTable.tooltips.platform")}>
              <CompactTag color="blue">{record.platform}</CompactTag>
            </Tooltip>
          )}
        </CompactSpace>
      );
    },
    [t]
  );

  const renderDate = useCallback(
    (record: Transfer) => (
      <Text type="secondary" style={{ fontSize: "12px" }}>
        {format(new Date(record.dateTime), "dd/MM/yy HH:mm")}
      </Text>
    ),
    []
  );

  const renderAmount = useCallback(
    (record: Transfer) => (
      <CompactSpace direction="vertical" size={0}>
        <Text strong style={{ color: "#389e0d" }}>
          ${record.amount.toFixed(2)}
        </Text>
        {record.validatedAmount &&
          record.validatedAmount !== record.amount && (
            <Tooltip
              title={t("wallet.transferTable.tooltips.validatedAmount")}
            >
              <CompactTag color="orange">
                Val: ${record.validatedAmount.toFixed(2)}
              </CompactTag>
            </Tooltip>
          )}
      </CompactSpace>
    ),
    [t]
  );

  const renderStatus = useCallback(
    (record: Transfer) => (
      <CompactSpace>
        {record.validatedReceiptHash ? (
          <CompactTag icon={<CheckCircleOutlined />} color="success">
            {t("wallet.transferTable.tags.validated")}
          </CompactTag>
        ) : record.isDiscontinued ? (
          <CompactTag icon={<StopOutlined />} color="error">
            {t("wallet.transferTable.tags.discontinued")}
          </CompactTag>
        ) : (
          <CompactTag icon={<ClockCircleOutlined />}>
            {t("wallet.transferTable.tags.pending")}
          </CompactTag>
        )}
        {record.linkedOperations.length > 0 && (
          <Tooltip
            title={t("wallet.transferTable.tooltips.linkedOperations", { count: record.linkedOperations.length })}
          >
            <CompactTag icon={<LinkOutlined />} color="cyan">
              {record.linkedOperations.length}
            </CompactTag>
          </Tooltip>
        )}
      </CompactSpace>
    ),
    [t]
  );

  const renderScore = useCallback(
    (record: Transfer) =>
      record.validationScore ? (
        <Tooltip title={t("wallet.transferTable.tooltips.validationScore")}>
          <CompactTag
            color={record.validationScore > 0.8 ? "green" : "orange"}
            style={{ width: "45px", textAlign: "center" }}
          >
            {Math.round(record.validationScore * 100)}%
          </CompactTag>
        </Tooltip>
      ) : null,
    [t]
  );

  const columns = useMemo(
    () => [
      {
        title: t("wallet.transferTable.table.operation"),
        key: "operation",
        width: 200,
        render: renderOperation,
      },
      {
        title: t("wallet.transferTable.table.date"),
        key: "date",
        width: 100,
        render: renderDate,
      },
      {
        title: t("wallet.transferTable.table.amount"),
        key: "amount",
        width: 120,
        render: renderAmount,
      },
      {
        title: t("wallet.transferTable.table.platform"),
        key: "platform",
        width: 150,
        render: renderPlatform,
      },
      {
        title: t("wallet.transferTable.table.status"),
        key: "status",
        width: 150,
        render: renderStatus,
      },
      {
        title: t("wallet.transferTable.table.score"),
        key: "score",
        width: 80,
        render: renderScore,
      },
      {
        title: t("wallet.transferTable.table.actions"),
        key: "actions",
        width: 120,
        render: (record: Transfer) => (
          <Button
            type="text"
            size="small"
            danger={!record.isDiscontinued}
            icon={record.isDiscontinued ? <UndoOutlined /> : <StopOutlined />}
            loading={discontinuingId === record.id}
            onClick={(e) => {
              e.stopPropagation();
              handleDiscontinueToggle(record.id, !!record.isDiscontinued);
            }}
            disabled={
              !!record.validatedReceiptHash || !!record.linkedOperations.length
            }
            title={
              record.validatedReceiptHash
                ? t("wallet.transferTable.tooltips.cannotModifyValidated")
                : record.linkedOperations.length
                ? t("wallet.transferTable.tooltips.cannotModifyLinked")
                : record.isDiscontinued
                ? t("wallet.transferTable.tooltips.restoreTransfer")
                : t("wallet.transferTable.tooltips.discontinueTransfer")
            }
          >
            {record.isDiscontinued
              ? t("wallet.transferTable.buttons.restore")
              : t("wallet.transferTable.buttons.discontinue")}
          </Button>
        ),
      },
    ],
    [
      renderOperation,
      renderDate,
      renderAmount,
      renderPlatform,
      renderStatus,
      renderScore,
      handleDiscontinueToggle,
      discontinuingId,
      t
    ]
  );

  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
  
// In TransferTable.tsx - Update the expandedRowRender function

const expandableConfig = useMemo(() => {
  const expandedRowRender = (record: Transfer) => {
    if (!record.linkedRequests?.length) return null;

    const relatedRequest = record.linkedRequests[0];
    const stableKey = `expand-${record.id}`;

    // IMPORTANT: Store only the original filename without any manipulation
    let receiptPath = relatedRequest.receipt || record.validatedReceiptHash || '';
    
    return (
      <div style={{ padding: "0 48px" }} key={stableKey}>
        <ProcessSteps
          record={{
            id: relatedRequest.id,
            type: relatedRequest.type,
            status: relatedRequest.status,
            amount: relatedRequest.amount,
            createdAt: relatedRequest.createdAt,
            // Pass exactly the same receipt path without modification
            receipt: receiptPath,
            requestData: relatedRequest.requestData,
            linkedTransfer: {
              ...record,
              validatedReceiptHash: record.validatedReceiptHash
            },
            adminId: effectiveAdminId,
            user: {
              adminId: effectiveAdminId
            }
          }}
        />
      </div>
    );
  };

  return {
    expandedRowRender,
    rowExpandable: (record: Transfer) => record.linkedRequests?.length > 0,
    expandedRowKeys,
    // Fix: convert the React state setter to match the expected function signature
    onExpandedRowsChange: (keys: readonly React.Key[]) => setExpandedRowKeys([...keys])
  };
}, [expandedRowKeys, effectiveAdminId]);


const handleExpand = useCallback((expanded: boolean, record: Transfer) => {
  setExpandedRowKeys(prev => 
    expanded 
      ? [...prev, record.id] 
      : prev.filter(key => key !== record.id)
  );
  
  // Clear any cached image when collapsing to ensure fresh load next time
  if (!expanded) {
    // This will help ensure image is refetched on next expansion
  }
}, []);

  // Modal para selección de plataforma
  const renderPlatformModal = () => {
    if (!currentPlatformTransfer) return null;
    
    return (
      <Modal
        title={t("wallet.transferTable.platformSelect.title")}
        open={platformModalVisible}
        onCancel={() => {
          setPlatformModalVisible(false);
          setCurrentPlatformTransfer(null);
          setCustomPlatform("");
        }}
        footer={[
          <Button 
            key="cancel" 
            onClick={() => {
              setPlatformModalVisible(false);
              setCurrentPlatformTransfer(null);
              setCustomPlatform("");
            }}
          >
            {t("common.cancel")}
          </Button>,
          <Button 
            key="submit" 
            type="primary" 
            loading={updatingPlatform}
            onClick={() => {
              if (customPlatform && currentPlatformTransfer) {
                handlePlatformChange(currentPlatformTransfer.id, customPlatform);
              }
            }}
            disabled={!customPlatform}
          >
            {t("common.save")}
          </Button>
        ]}
        width={isTabletOrMobile ? 320 : 400}
        centered
      >
        <div style={{ padding: '16px 0' }}>
          <div style={{ marginBottom: 16 }}>
            <Text strong>{t("wallet.transferTable.platformSelect.selectPlatform")}</Text>
          </div>
          
          {/* Lista de plataformas predefinidas */}
          <div style={{ marginBottom: 16, maxHeight: '200px', overflowY: 'auto', padding: '0 4px' }}>
            <Space direction="vertical" style={{ width: '100%' }} size={8}>
              {PLATFORM_OPTIONS.map((platform) => (
                <Button 
                  key={platform} 
                  style={{ width: '100%', textAlign: 'left' }}
                  onClick={() => {
                    handlePlatformChange(currentPlatformTransfer.id, platform);
                  }}
                >
                  {platform}
                </Button>
              ))}
              
              {/* Plataformas personalizadas usadas previamente */}
              {localTransfers
                .map((t) => t.platform)
                .filter(
                  (platform): platform is string =>
                    Boolean(platform) &&
                    !PLATFORM_OPTIONS.includes(
                      platform as (typeof PLATFORM_OPTIONS)[number]
                    )
                )
                .filter(
                  (platform, index, self) => self.indexOf(platform) === index
                )
                .map((platform) => (
                  <Button 
                    key={platform} 
                    style={{ width: '100%', textAlign: 'left' }}
                    onClick={() => {
                      handlePlatformChange(currentPlatformTransfer.id, platform);
                    }}
                  >
                    {platform}
                  </Button>
                ))}
            </Space>
          </div>
          
          {/* Input para plataforma personalizada */}
          <div style={{ marginTop: 16 }}>
            <Text strong>{t("wallet.transferTable.platformSelect.customPlatform")}</Text>
            <Input
              style={{ marginTop: 8 }}
              placeholder={t("wallet.transferTable.platformSelect.enterCustomPlatform")}
              value={customPlatform}
              onChange={(e) => setCustomPlatform(e.target.value)}
              onPressEnter={() => {
                if (customPlatform && currentPlatformTransfer) {
                  handlePlatformChange(currentPlatformTransfer.id, customPlatform);
                }
              }}
            />
          </div>
        </div>
      </Modal>
    );
  };

  return (
    <>
{isTabletOrMobile ? (
  <div style={{ marginBottom: '200px' }}>
    {localTransfers.map(transfer => (
      <MobileTransferCard key={transfer.id} transfer={transfer} />
    ))}
    
    {isLoading && (
      <div style={{ textAlign: 'center', padding: 20 }}>
        <Text>{t("common.loading")}</Text>
      </div>
    )}
    
    {/* Add pagination for mobile view */}
{pagination && (
  <div style={{ 
    padding: '16px 8px', 
    marginBottom: '300px',  // Increase this value
    paddingBottom: '100px', // Add padding for additional space
    display: 'flex', 
    justifyContent: 'center',
    marginTop: 16
  }}>
        <Pagination
          current={pagination.current}
          pageSize={pagination.pageSize}
          total={pagination.total}
          onChange={(page, pageSize) => {
            if (pagination.onChange) {
              pagination.onChange(page, pageSize);
            }
          }}
          size="small"
          showSizeChanger={pagination.showSizeChanger}
          style={{
            textAlign: 'center',
            width: '100%'
          }}
        />
      </div>
    )}
  </div>
) : (
  // Existing desktop view code
  <div style={{ marginBottom: '50px' }}>
    <CompactTable
      dataSource={localTransfers}
      columns={columns}
      rowKey={getRowKey}
      size="small"
      pagination={pagination}
      loading={isLoading}
      scroll={{ x: 920 }}
      expandable={expandableConfig}
      onExpand={handleExpand}
    />
  </div>
)}
      
      {renderPlatformModal()}
    </>
  );
};

export default TransferTable;